import React from 'react';

// İt200 (2026-05-21) — Karargah onaylı ChunkLoadError kalkanı.
// Saha peş peşe build aldıkça eski chunk hash'leri arayan tarayıcılar
// `Loading chunk N failed` ile React render aşamasında patlıyordu. Burada
// detect + sessizce hard reload + sessionStorage ile tek-seferlik koruma.
//
// Aynı KEY index.html'daki dahili recovery script ile PAYLAŞILIYOR
// (KEY_CHUNK = `wms_recovered_chunk_<path>`) — App mount sonrası orada
// otomatik temizleniyor, yani ikinci recovery turuna izin verilmez.
function _chunkRecoveryKey() {
  try {
    return 'wms_recovered_chunk_' + (window.location.pathname || '/');
  } catch (_) {
    return 'wms_recovered_chunk_default';
  }
}

function _isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = String((error && (error.message || error.toString())) || '');
  return (
    /Loading chunk\s+[\w-]+\s+failed/i.test(msg) ||
    /Loading CSS chunk\s+[\w-]+\s+failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

// İt201/İt204 (2026-05-21) — Zebra/eski WebView defansif yardımcılar.
// sessionStorage Safari private mode + Zebra kısıtlı WebView'da throw atabilir.
// İt204: URL parametre `?_retry=1` BIRINCIL sonsuz loop kırıcı (location.href.indexOf,
// ES3-uyumlu). sessionStorage ikincil best-effort kalır.
function _alreadyRetried() {
  try {
    const href = String((typeof window !== 'undefined' && window.location && window.location.href) || '');
    return href.indexOf('_retry=1') !== -1 || href.indexOf('_retry%3D1') !== -1;
  } catch (_) { return false; }
}
function _sessSafeGet(k) {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(k) : null; } catch (_) { return null; }
}
function _sessSafeSet(k, v) {
  try { if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(k, v); } catch (_) {}
}
function _safeReload() {
  // İt204: SONSUZ LOOP KIRICI — URL'de zaten _retry=1 varsa reload TETİKLEME.
  // ErrorBoundary getDerivedStateFromError → recovered=false dönderir → fallback UI.
  if (_alreadyRetried()) {
    try { console.warn('[İt204] _safeReload aborted — URL\'de zaten _retry=1, sonsuz loop kırıldı.'); } catch (_) {}
    return false;
  }
  // URL constructor eski WebView'da olmayabilir → manuel query string append.
  try {
    if (typeof URL === 'function') {
      const u = new URL(window.location.href);
      u.searchParams.set('_retry', '1');
      u.searchParams.set('_v', String(Date.now()));
      window.location.replace(u.toString());
      return true;
    }
  } catch (_) {}
  // ES3 fallback: manuel string concat.
  try {
    const href = String(window.location.href || '');
    const sep = href.indexOf('?') === -1 ? '?' : '&';
    const newHref = href + sep + '_retry=1&_v=' + Date.now();
    if (window.location.replace) {
      window.location.replace(newHref);
    } else {
      window.location.href = newHref;
    }
    return true;
  } catch (_) {}
  try { window.location.reload(true); return true; } catch (_) {
    try { window.location.href = window.location.href; return true; } catch (__) {}
  }
  return false;
}

function _triggerChunkRecovery(error) {
  // İt204: URL kilit BIRINCIL. sessionStorage helper'ı best-effort kalır (Zebra'da tutulmasa
  // bile URL parametre tutarlıdır).
  if (_alreadyRetried()) {
    console.warn('[İt200/İt204] ChunkLoadError ikinci kez (URL\'de _retry=1) — fallback UI.');
    return false;
  }
  try {
    const KEY = _chunkRecoveryKey();
    _sessSafeSet(KEY, String(Date.now())); // best-effort (Zebra'da no-op olabilir)
    console.warn('[İt200] ChunkLoadError yakalandı, sessizce yenileniyor:', error && error.message);
    // İt201: Promise + caches API her ikisi de Zebra Android 4.x WebView'da YOK olabilir.
    try {
      if (
        typeof window !== 'undefined' &&
        'caches' in window &&
        window.caches &&
        typeof window.caches.keys === 'function' &&
        typeof Promise !== 'undefined'
      ) {
        const p = window.caches.keys();
        if (p && typeof p.then === 'function') {
          p
            .then((keys) => {
              try { return Promise.all(keys.map((k) => window.caches.delete(k))); }
              catch (_) { return null; }
            })
            .then(_safeReload, _safeReload);
          return true;
        }
      }
    } catch (_) {}
    return _safeReload();
  } catch (e) {
    try { console.error('[İt200] _triggerChunkRecovery internal failure:', e); } catch (_) {}
    return _safeReload();
  }
}

// İt413 (2026-06-09) — Karargah HAM LOG zırhı.
// 'Konsol boş, hata yutuluyor' şikayeti üzerine console.error AÇIK +
// localStorage'a son hata mesajı + componentStack ilk 600 char saklanır.
// SAHA komutanı 'localStorage.getItem("wms_last_error")' ile raporlayabilir.
function _logErrorLoud(error, errorInfo) {
  try {
    const msg = (error && (error.message || String(error))) || 'unknown';
    const stack = (error && error.stack) || '';
    const compStack = (errorInfo && errorInfo.componentStack) || '';
    /* eslint-disable no-console */
    console.error('[İt413/ErrorBoundary] HATA YAKALANDI:', msg);
    console.error('[İt413/ErrorBoundary] stack:', stack.slice(0, 800));
    if (compStack) {
      console.error('[İt413/ErrorBoundary] componentStack:', compStack.slice(0, 800));
    }
    /* eslint-enable no-console */
    try {
      const payload = JSON.stringify({
        message: msg,
        stack: stack.slice(0, 600),
        componentStack: compStack.slice(0, 600),
        ts: new Date().toISOString(),
        path: (typeof window !== 'undefined' && window.location && window.location.pathname) || '',
      });
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('wms_last_error', payload);
      }
    } catch (_) { /* localStorage Safari private mode throws */ }
  } catch (_) { /* defansif — ErrorBoundary asla patlamamalı */ }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // ResizeObserver errors are harmless, ignore them
    if (error?.message?.includes('ResizeObserver')) {
      return { hasError: false };
    }
    // İt200: ChunkLoadError — kırmızı hata ekranı YERİNE sessizce reload.
    // hasError=false döner, recovery zaten reload tetikleyecek (kullanıcı kısa süre boş alan görür).
    if (_isChunkLoadError(error)) {
      const recovered = _triggerChunkRecovery(error);
      if (recovered) return { hasError: false };
      // Recovery yapılmadıysa (ikinci defa) — normal fallback UI göster
      return { hasError: true };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Don't log ResizeObserver errors
    if (error?.message?.includes('ResizeObserver')) {
      return;
    }
    // İt200: ChunkLoadError'u normal error path'inden ayrı tut (zaten getDerived'da işlendi).
    if (_isChunkLoadError(error)) {
      return;
    }
    // İt413 (2026-06-09) — Karargah HAM LOG emri: console.error + localStorage.
    // Eski İt252 silent log konsolu boş bırakıyordu, kör uçuş. Şimdi:
    // - console.error (warn değil) → tarayıcı dev tools'ta kırmızı görünür
    // - localStorage 'wms_last_error' → komutan rapor edebilir
    // - state.error → fallback UI'da kısa mesaj göster
    _logErrorLoud(error, errorInfo);
    try {
      this.setState({ error: error, errorInfo: errorInfo });
    } catch (_) { /* defansif */ }
  }

  render() {
    if (this.state.hasError) {
      // İt252 — Karargah onaylı SADE FALLBACK. error.toString() + componentStack
      // ekran panel'i imha edildi; "Zebra: baglanti ve surum hatasi" diye yorumlanan
      // teknik error text artık görünmüyor.
      // İt413 — Karargah HAM LOG emri: teknik mesaj göster (saha komutanı
      // sahnede ham mesajı doğrudan görebilir, console.error + localStorage
      // korunur).
      const _errMsg = (this.state.error && (this.state.error.message || String(this.state.error))) || '';
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-lg">
            <h1 className="text-xl font-semibold text-slate-800 mb-3">Sayfa Yeniden Yüklenmeli</h1>
            <p className="text-slate-500 text-sm mb-3">Lütfen yenileyin.</p>
            {_errMsg && (
              <details className="text-left bg-red-50 border border-red-200 rounded p-2 mb-4 text-xs text-red-800">
                <summary className="cursor-pointer font-mono">Teknik detay (raporlayın)</summary>
                <pre className="whitespace-pre-wrap break-words mt-2 max-h-32 overflow-y-auto">{_errMsg.slice(0, 400)}</pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Sayfayı Yenile
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
